/**
 * Snapshot of the page the user is looking at, handed to the copilot so it can
 * answer "what is this field for?" about THIS screen.
 *
 * Privacy line: labels and help text only. Field VALUES are never read — a
 * copilot question on a lead page must not ship that person's phone number to
 * the AI provider.
 */
import { guideForRoute } from './pageGuide';

export interface PageContext {
  route: string;
  title?: string;
  breadcrumb?: string;
  /** What this screen is for, from the app's own page guide. */
  purpose?: string;
  actions?: string[];
  fields: Array<{ label: string; hint?: string }>;
}

const MAX_FIELDS = 40;

export function collectPageContext(route: string): PageContext {
  const main = document.querySelector('main') ?? document.body;

  const title = main.querySelector('h1')?.textContent?.trim() || document.title || undefined;
  // The Topbar renders the trail just above the title.
  const breadcrumb = main.querySelector('h1')?.previousElementSibling?.textContent?.trim() || undefined;

  const fields: PageContext['fields'] = [];
  const seen = new Set<string>();
  main.querySelectorAll<HTMLElement>('[data-copilot-field]').forEach((el) => {
    if (fields.length >= MAX_FIELDS) return;
    // Skip anything not currently visible — collapsed tabs and closed accordions
    // would otherwise describe fields the user cannot see.
    if (el.offsetParent === null) return;
    const label = el.dataset.copilotField?.trim();
    if (!label || seen.has(label)) return;
    seen.add(label);
    fields.push({ label, hint: el.dataset.copilotHint?.trim() || undefined });
  });

  const guide = guideForRoute(route);
  return { route, title, breadcrumb, purpose: guide?.purpose, actions: guide?.actions, fields };
}

/** Two or three things worth asking about on this page, for the empty state. */
export function starterQuestions(ctx: PageContext): string[] {
  const out: string[] = [];
  const withHints = ctx.fields.filter((f) => f.hint);
  if (withHints[0]) out.push(`What does "${withHints[0].label}" do?`);
  if (withHints[1]) out.push(`What should I put in "${withHints[1].label}"?`);
  if (ctx.purpose || ctx.title) out.push('What is this page for?');
  if (ctx.actions?.[0]) out.push(`How do I ${ctx.actions[0].charAt(0).toLowerCase()}${ctx.actions[0].slice(1)}?`);
  return out.slice(0, 3);
}
